import * as ManagedRuntime from "effect/ManagedRuntime";
import type * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import { FetchHttpClient } from "effect/unstable/http";

import { environmentHttpClientLayer } from "@t3tools/client-runtime";
import { currentServerToken } from "../environments/primary/serverToken";
import {
  PrimaryEnvironmentHttpClient,
  primaryEnvironmentHttpClientLive,
} from "../environments/primary/httpClient";
import { primaryEnvironmentRequestInit } from "../environments/primary/requestInit";
import { readWorkspaceRefusal, reloadOnce } from "../environments/primary/workspaceState";
import { onWebAddress } from "../shared/auth/web-address";

const webHttpClientLayer = environmentHttpClientLayer(globalThis.fetch);

export const remoteHttpRuntime = ManagedRuntime.make(webHttpClientLayer);

/**
 * Attaches `Authorization: Bearer <token>` to every outgoing primary-environment
 * (server) HTTP request — the single place this header is set. When no token is
 * held (never signed in, or the token was cleared), the header is omitted
 * entirely: the server then fails closed with a normal
 * 401, whereas an empty bearer value would read as a malformed credential and
 * surface as a server fault instead.
 */
export const primaryEnvironmentFetch: typeof globalThis.fetch = async (input, init) => {
  const token = currentServerToken();
  let response: Response;
  if (token === null) {
    response = await globalThis.fetch(input, init);
  } else {
    const headers = new Headers(init?.headers);
    headers.set("Authorization", `Bearer ${token}`);
    response = await globalThis.fetch(input, { ...init, headers });
  }
  if (onWebAddress()) void reloadOnWorkspaceRefusal(response);
  return response;
};

/**
 * On the web address, a router refusal mid-session (the session ended, lost
 * its access, or its workspace stopped or failed) reloads the page, whose gate
 * then shows what the router says. Every server request carries the session,
 * so the session poll and the socket's reconnects notice within a minute.
 */
async function reloadOnWorkspaceRefusal(response: Response): Promise<void> {
  if ((await readWorkspaceRefusal(response)) !== null) reloadOnce();
}

const primaryHttpRuntime = ManagedRuntime.make(
  primaryEnvironmentHttpClientLive.pipe(
    Layer.provide(
      Layer.mergeAll(
        environmentHttpClientLayer(primaryEnvironmentFetch),
        Layer.succeed(FetchHttpClient.RequestInit, primaryEnvironmentRequestInit),
      ),
    ),
  ),
);

export type PrimaryHttpEffectRunner = <A, E>(
  effect: Effect.Effect<A, E, PrimaryEnvironmentHttpClient>,
) => Promise<A>;

const livePrimaryHttpRunner: PrimaryHttpEffectRunner = (effect) =>
  primaryHttpRuntime.runPromise(effect);

let primaryHttpRunner = livePrimaryHttpRunner;

export const runPrimaryHttp = <A, E>(effect: Effect.Effect<A, E, PrimaryEnvironmentHttpClient>) =>
  primaryHttpRunner(effect);

export function __setPrimaryHttpRunnerForTests(runner?: PrimaryHttpEffectRunner): void {
  primaryHttpRunner = runner ?? livePrimaryHttpRunner;
}

export const webRuntime = ManagedRuntime.make(webHttpClientLayer);
